import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";

import { Btn } from "../../lib/Btn";
import { Card } from "../../lib/Card";
import { Icon } from "../../lib/Icon";
import { Input, Textarea } from "../../lib/Input";
import { Pill } from "../../lib/Pill";
import { Slats } from "../../lib/Slats";
import { TaxNote } from "../../lib/TaxNote";
import { Toggle } from "../../lib/Toggle";
import { intake, pricing, type PlanLadderItem } from "../../lib/api";
import { CURRENCIES } from "../../lib/countries";
import { useLocale } from "../../lib/locale";
import { currencySymbol, wholeUnits } from "../../lib/money";
import { withUtm } from "../../lib/utm";
import { faqJsonLd, useSeo } from "../../lib/useSeo";
import { cn } from "../../lib/cn";

const FAQ = [
  ["Which countries and states does NestPlot work in?", "NestPlot sells in the US (all 50 states + DC), Canada, the UK, Ireland, Australia, and New Zealand — priced in your local currency. Inside the US, the moment you create a property NestPlot auto-generates a lease template from the statute that governs your vertical (landlord-tenant act for multifamily, self-storage statute for storage, parking lease law for lots, marina/RV agreement for vehicle storage), with UETA/E-SIGN signing in every state. Outside the US, you run the full platform — listings, online rent through your own Stripe, the tenant portal, access control, maintenance, reporting — but you bring your own lease documents: the statute-cited lease/lien automation is US-only today."],
  ["Do I need a card to start?", "No. The 30-day trial requires no card. We ask for a card when you decide to keep using NestPlot."],
  ["I only own one property — is NestPlot overkill?", "No. Starter is built for a single landlord or a single-facility owner: online rental, e-sign, autopay, and access-vendor integration on day one, at $59/mo flat. You get the same engine the large managers run, sized (and priced) for one property."],
  ["I run dozens of properties across multiple entities — can NestPlot handle that?", "Yes. Portfolio covers up to 50 properties with multi-entity accounting (per LLC), cross-property dashboards, and Pricing AI included. Above that, Enterprise adds unlimited properties, SSO, the public API, and dedicated onboarding + support. NestPlot scales from one door to a nationwide portfolio on the same platform."],
  ["Will my access vendor work?", "Self-storage: PTI Falcon, SecurGuard, INSOMNIAC CIA (OpenTech), Noke, DaVinciLock, and Janus ship today. Multifamily: major fob and mobile-key vendors. Parking: LPR + QR-validation. Marina/RV: gated dock and lot access. Reach out if you don't see yours and we'll confirm the integration path."],
  ["What stays on the operator's shoulders, compliance-wise?", "A few things stay with you, regardless of vertical: (a) any insurance-licensed add-on you offer alongside the lease — tenant-protection plans in self-storage (NY/CA/FL treat them as insurance), renter's insurance referrals in multifamily, hull insurance verification in marinas — NestPlot captures the customer's election but does not underwrite; (b) sales tax, parking surcharges, and any other transaction tax — NestPlot calculates and itemizes the right amount, the operator files the return; (c) the final step of any recovery action — court eviction filing in multifamily, lien-sale auction in self-storage (currently routed through third-party platforms), tow/impound in parking, vessel detention or auction in marinas — runs through the operator and their service providers. NestPlot drives the notice windows and produces the required documents up to that step."],
  ["How do my tenants pay, and what does it cost me?", "Tenants pay from their portal by **bank account (ACH)** or card — bank is the recommended default because it's far cheaper. ACH runs at **0.8%, capped at $5.00** per payment; cards at **2.9% + $0.30**. On $1,800 rent that's about **$5 by bank vs. ~$52 by card**, and either way the money settles straight to your own bank — NestPlot takes 0% of rent. Bank verification is instant (the tenant signs into their bank through Stripe); a card posts immediately while a bank payment settles in 1–2 business days. Autopay works with a saved bank account or card."],
  ["What if I cancel?", "All your customer data exports as CSV. Your data is yours — we never hold it hostage."],
  ["Are there any add-on fees?", "The software subscription is flat — all features included, including SMS to tenants (Twilio is fully covered by NestPlot at any volume). There's one small carve-out: **two vendor pass-throughs** — real, direct costs NestPlot doesn't manufacture — charged to your card on file at the moment you use them, not aggregated to a month-end invoice. Background checks (Checkr, ~$30-50 per report) and certified mail for state-required notices (Lob, ~$8 per letter). We bill at wholesale plus a small platform markup. For background checks specifically, you can flip a per-facility toggle to pass the fee through to the applicant (Stripe payment link before the check runs) instead of eating it yourself. Your subscription covers the software; the two vendor pass-throughs are always billed at cost."],
] as const;

// Per-plan marketing copy (who it's for, feature bullets, which card is
// flagged). The NUMBERS no longer live here — they come from the public
// pricing ladder API (routes/public_intake.py GET /public/plans), which
// resolves the visitor's currency and returns the pinned PRICE_TABLE amounts.
// That kills the old hand-synced copy of the prices: one source
// (data/countries.py + data/plans.py), per-currency, correct at checkout.
type PlanKey = "solo" | "small" | "multi";
const PLAN_META: Record<
  PlanKey | "enterprise",
  { who: string; features: string[]; popular?: boolean }
> = {
  solo: {
    who: "A single independent landlord or one-facility owner.",
    features: [
      "Online rental / application + e-sign",
      "Autopay + automatic dunning",
      "Tenant / resident portal",
      "Standard access-vendor integration",
      "CSV import + state-compliant lease templates",
    ],
  },
  small: {
    who: "A growing owner-operator adding doors and staff.",
    popular: true,
    features: [
      "Everything in Starter",
      "Multi-property roster + staff seats",
      "Bulk SMS (fully covered by NestPlot)",
      "Custom lease templates",
      "Priority email support",
    ],
  },
  multi: {
    who: "A multi-property manager running several entities.",
    features: [
      "Everything in Growth",
      "Cross-property dashboards",
      "Multi-entity accounting (per LLC)",
      "Pricing AI included",
      "Custom domain on your customer portal",
      "Phone + Slack support",
    ],
  },
  enterprise: {
    who: "The largest managers and nationwide portfolios.",
    features: [
      "Everything in Portfolio",
      "SSO / SAML + advanced roles",
      "Public API + scoped API keys",
      "White-label email + portal domains",
      "Dedicated onboarding + SLA support",
    ],
  },
};

// USD ladder, for the SEO JSON-LD only (schema.org prices are conventionally
// USD and static). The live, currency-aware numbers render from the API.
const USD_LADDER = [59, 99, 199];

// Per-vertical benchmark context — kept for the "how our flat price compares"
// story and SEO. No per-vertical prices anymore: one ladder serves all.
type VerticalKey = "multifamily" | "self_storage" | "parking" | "rv_boat";
const VERTICAL_BENCHMARKS: {
  key: VerticalKey;
  label: string;
  accentVar: string;
  context: string;
}[] = [
  {
    key: "multifamily",
    label: "Multifamily",
    accentVar: "var(--multifamily-accent, #3F6B4B)",
    context:
      "AppFolio is ~$1.40/unit/mo with a $400 minimum; Buildium is $52 + per-unit add-ons. NestPlot is flat per-property — from a single 12-unit walkup to a 500-door portfolio, your per-unit cost drops as you grow.",
  },
  {
    key: "self_storage",
    label: "Self-storage",
    accentVar: "var(--storage-accent, #B5543B)",
    context:
      "storEDGE / SiteLink land around $300–500 per facility per month plus a per-transaction processing margin. NestPlot is flat whether you run one facility or a hundred, and you keep 100% of tenant rent.",
  },
  {
    key: "parking",
    label: "Parking + lots",
    accentVar: "var(--parking-accent, #6C5AA8)",
    context:
      "Enterprise parking platforms charge $500–2k per lot per month and bundle a convenience-fee margin on parker payments. NestPlot is flat per-lot from one garage to a citywide operation, and payments route through your own Stripe.",
  },
  {
    key: "rv_boat",
    label: "RV + boat / marinas",
    accentVar: "var(--vehicle-accent, #2D6E8F)",
    context:
      "Marina Office Manager and Speedwell run $200–400 per site per month. NestPlot is flat per-site — one dry-stack yard or a multi-marina group — with the dock-plan editor and vessel/RV asset capture included.",
  },
];

export function PricingPage() {
  useSeo({
    title: "Pricing — NestPlot · flat monthly for operators of every size",
    description:
      "One flat monthly price from a single landlord to the largest portfolios: Starter $59, Growth $99, Portfolio $199, Enterprise for unlimited. 0% of your customer rent — every payment lands in your bank via your own Stripe Connect. Multifamily, self-storage, parking, RV/boat.",
    path: "/pricing",
    // FAQ + SoftwareApplication with an AggregateOffer over the ladder
    // prices. AggregateOffer is the schema.org pattern for a product with
    // multiple priced tiers — Google surfaces the lowest ("From $59/mo").
    jsonLd: [
      faqJsonLd(FAQ.map(([q, a]) => ({ q, a }))),
      {
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        "@id": "https://nestplot.com/pricing",
        name: "NestPlot",
        description:
          "Property operations software for operators of every size across multifamily, self-storage, parking, and RV/boat.",
        applicationCategory: "BusinessApplication",
        operatingSystem: "Web",
        url: "https://nestplot.com/pricing",
        offers: {
          "@type": "AggregateOffer",
          priceCurrency: "USD",
          lowPrice: Math.min(...USD_LADDER).toString(),
          highPrice: Math.max(...USD_LADDER).toString(),
          offerCount: "4",
          availability: "https://schema.org/InStock",
        },
      },
    ],
  });
  const { t } = useLocale();
  const [annual, setAnnual] = useState(false);
  const [params, setParams] = useSearchParams();
  // Currency: explicit ?currency= wins; otherwise the API resolves it from the
  // Cloudflare edge country. Getting it wrong costs a visitor nothing — it's a
  // different ladder, not a different bill (the charge comes from their own
  // country at checkout).
  const currencyParam = params.get("currency")?.toUpperCase();
  const ladder = useQuery({
    queryKey: ["pricing", "plans", currencyParam ?? "auto"],
    queryFn: () => pricing.plans(currencyParam || undefined),
    staleTime: 5 * 60_000,
  });
  const currency = ladder.data?.currency ?? currencyParam ?? "USD";
  const sym = currencySymbol(currency, "en-US");

  function changeCurrency(next: string) {
    const p = new URLSearchParams(params);
    p.set("currency", next);
    setParams(p, { replace: true });
  }

  // Build the display cards from the API ladder, falling back to a quiet
  // skeleton of four while it loads so the layout doesn't jump.
  const rows: PlanLadderItem[] =
    ladder.data?.plans ?? [
      { plan: "solo", name: "Starter", units_blurb: "1 property · up to 50 units", monthly_cents: null, annual_cents: null, contact_only: false, pricing_ai_included: false },
      { plan: "small", name: "Growth", units_blurb: "Up to 10 properties", monthly_cents: null, annual_cents: null, contact_only: false, pricing_ai_included: false },
      { plan: "multi", name: "Portfolio", units_blurb: "Up to 50 properties · multi-entity", monthly_cents: null, annual_cents: null, contact_only: false, pricing_ai_included: true },
      { plan: null, name: "Enterprise", units_blurb: "Unlimited properties", monthly_cents: null, annual_cents: null, contact_only: true, pricing_ai_included: true },
    ];

  return (
    <div className="mx-auto max-w-[1320px] px-5 lg:px-8 py-12 lg:py-16">
      <div className="text-center max-w-[760px] mx-auto">
        <Pill tone="accent" size="sm">
          {t("pricing.eyebrow")}
        </Pill>
        <h1 className="t-display-xl mt-4">{t("pricing.title")}</h1>
        <p className="text-sub text-lg mt-3">
          Pay a flat monthly fee for the software. Keep <strong>every dollar</strong>
          {" "}your tenants, residents, boaters, or permit-holders pay you. We
          route every payment through your own Stripe Connect — we literally
          cannot take a cut, by design. Same engine whether you own a single
          building or manage a nationwide portfolio. 30-day trial. No card up front.
        </p>
        <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
          <div className="inline-flex items-center gap-3 bg-paper border border-line rounded-full px-3 py-[6px]">
            <span className={cn("text-sm font-semibold", !annual && "text-ink")}>{t("pricing.monthly")}</span>
            <Toggle on={annual} onChange={setAnnual} />
            <span className={cn("text-sm font-semibold flex items-center gap-2", annual && "text-ink")}>
              {t("pricing.annual")} <Pill tone="ok" size="sm">{t("pricing.annualSave")}</Pill>
            </span>
          </div>
          <label className="inline-flex items-center gap-2 text-sm text-sub">
            {t("pricing.currencyLabel")}
            <select
              value={currency}
              onChange={(e) => changeCurrency(e.target.value)}
              className="rounded-full border border-line bg-paper px-3 py-[6px] text-ink font-semibold focus:outline-none"
            >
              {CURRENCIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mt-10">
        {rows.map((tier) => {
          const meta = PLAN_META[(tier.plan ?? "enterprise") as PlanKey | "enterprise"];
          const isContact = tier.contact_only;
          const popular = !!meta?.popular;
          const monthly = tier.monthly_cents;
          const annualCents = tier.annual_cents;
          const shown =
            !isContact && annual && annualCents != null
              ? Math.round(wholeUnits(annualCents, currency) / 12)
              : monthly != null
                ? wholeUnits(monthly, currency)
                : null;
          const accent = "var(--accent)";
          return (
            <Card
              key={tier.name}
              className={cn(popular && "shadow-rollup")}
              style={popular ? { border: `1.5px solid ${accent}` } : undefined}
            >
              <div
                className="px-5 pt-3 pb-2"
                style={{ backgroundColor: popular ? accent : "var(--surface, #f6f3ee)" }}
              >
                <Slats
                  color={popular ? "var(--on-accent, #fff)" : "var(--ink)"}
                  opacity={popular ? 0.55 : 0.4}
                  height={10}
                />
              </div>
              <div className="px-6 py-6">
                {popular && (
                  <div className="mb-2">
                    <Pill tone="accent" size="sm">
                      {t("pricing.mostOperators")}
                    </Pill>
                  </div>
                )}
                <div className="font-display font-extrabold text-[22px] uppercase tracking-[-0.01em]">
                  {tier.name}
                </div>
                <div className="text-sub text-sm mt-1">{tier.units_blurb}</div>
                <div className="text-sub text-xs mt-1 min-h-[32px]">{meta?.who}</div>
                {isContact ? (
                  <div className="mt-5 flex items-end gap-2">
                    <span className="font-display font-black text-[38px] tracking-[-0.03em] leading-none">
                      {t("pricing.contactUs")}
                    </span>
                  </div>
                ) : (
                  <>
                    <div className="mt-5 flex items-end gap-2">
                      <span className="font-display font-black text-[56px] tracking-[-0.035em] leading-none">
                        {shown == null ? "—" : `${sym}${shown}`}
                      </span>
                      <span className="text-sub mb-2">{t("pricing.perMonth")}</span>
                    </div>
                    <div className="text-sub text-xs font-mono mt-1">
                      {annual && annualCents != null
                        ? t("pricing.billedAnnually", { amount: `${sym}${wholeUnits(annualCents, currency)}` })
                        : t("pricing.monthlyCancel")}
                    </div>
                  </>
                )}

                <ul className="mt-5 space-y-2 text-sm">
                  {(meta?.features ?? []).map((f) => (
                    <li key={f} className="flex items-start gap-2">
                      <Icon name="check" size={14} className="text-ok mt-[3px] shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>

                {isContact ? (
                  <a href="#contact-sales" className="block mt-6">
                    <Btn kind="secondary" full size="md">
                      {t("pricing.contactSales")}
                    </Btn>
                  </a>
                ) : (
                  <Link to={`/signup?plan=${tier.plan}`} className="block mt-6">
                    <Btn kind={popular ? "primary" : "secondary"} full size="md">
                      {t("pricing.startTrial")}
                    </Btn>
                  </Link>
                )}
              </div>
            </Card>
          );
        })}
      </div>

      <div className="mt-6 max-w-[680px] mx-auto">
        <TaxNote currency={currency} className="text-center" />
      </div>

      <p className="text-center text-sub text-sm mt-4 max-w-[680px] mx-auto">
        Every tier includes all four verticals — multifamily, self-storage,
        parking, and RV/boat — and every core feature. Tiers differ only by how
        many properties you run.
      </p>

      <section className="mt-16 border-t border-line pt-14">
        <div className="max-w-[760px]">
          <div className="font-mono text-[11px] text-sub tracking-[0.14em] uppercase">
            How the flat price compares
          </div>
          <h2 className="t-display-l mt-2">Cheaper than the incumbent in every vertical.</h2>
          <p className="text-sub mt-3 max-w-[640px]">
            The legacy platforms meter you per unit, per facility, or skim a
            processing margin off your customers' payments. NestPlot is one flat
            price and 0% of the rent — so the bigger you get, the more you save.
          </p>
        </div>
        <div className="grid md:grid-cols-2 gap-4 mt-8">
          {VERTICAL_BENCHMARKS.map((v) => (
            <Card key={v.key}>
              <div className="px-6 py-5">
                <div
                  className="font-mono text-[11px] tracking-[0.18em] uppercase"
                  style={{ color: v.accentVar }}
                >
                  {v.label}
                </div>
                <p className="text-sub text-sm mt-2">{v.context}</p>
              </div>
            </Card>
          ))}
        </div>
      </section>

      <div className="mt-16 border-t border-line pt-14 grid md:grid-cols-3 gap-4">
        <Card>
          <div className="px-5 py-5">
            <div className="font-display font-extrabold text-[18px] uppercase tracking-[-0.01em]">
              30-day free trial
            </div>
            <p className="text-sub text-sm mt-2">
              No card up front. Migrate your roster as a CSV and connect your
              access vendor; you only pay if you stay past day 30.
            </p>
          </div>
        </Card>
        <Card>
          <div className="px-5 py-5">
            <div className="font-display font-extrabold text-[18px] uppercase tracking-[-0.01em]">
              Pricing AI add-on
            </div>
            <p className="text-sub text-sm mt-2">
              {sym}
              {wholeUnits(ladder.data?.pricing_ai_monthly_cents ?? 2900, currency)}/mo
              {" "}on Starter and Growth. Included free on Portfolio and
              Enterprise. Advisory by default — you approve every change.
            </p>
          </div>
        </Card>
        <Card>
          <div className="px-5 py-5">
            <div className="font-display font-extrabold text-[18px] uppercase tracking-[-0.01em]">
              Migration included
            </div>
            <p className="text-sub text-sm mt-2">
              <strong>Migration is included on every plan</strong> — no setup
              fee. The NestPlot team moves your roster over with you in one
              session, and you start on a 30-day free trial, no card.{" "}
              <Link to="/pilot" className="text-accent">Plan your migration →</Link>
            </p>
          </div>
        </Card>
      </div>

      <div id="contact-sales" className="mt-16 border-t border-line pt-14">
        <div className="font-mono text-[11px] text-sub tracking-[0.14em] uppercase">
          Enterprise &amp; large portfolios
        </div>
        <h2 className="t-display-l mt-2">Contact sales</h2>
        <p className="text-sub mt-2 max-w-[600px]">
          For unlimited properties, SSO / SAML, the public API, white-label
          domains, dedicated onboarding, or anything that doesn't fit the
          self-serve flow. Prefer to see it live?{" "}
          <Link to="/book-demo" className="text-accent font-semibold">Book a demo →</Link>{" "}
          We typically respond within one business day.
        </p>
        <div className="mt-6">
          <ContactSalesForm />
        </div>
      </div>

      <div className="mt-16 border-t border-line pt-14">
        <div className="font-mono text-[11px] text-sub tracking-[0.14em] uppercase">
          Common questions
        </div>
        <h2 className="t-display-l mt-2">FAQ</h2>
        <div className="mt-6 grid md:grid-cols-2 gap-3">
          {FAQ.map(([q, a]) => (
            <Card key={q}>
              <div className="px-5 py-4">
                <div className="font-semibold">{q}</div>
                <p className="text-sub text-sm mt-2">{a}</p>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}

function ContactSalesForm() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [facilities, setFacilities] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !message.trim()) return;
    setSubmitting(true);
    try {
      await intake.submitInquiry(withUtm({
        name: name.trim(),
        email: email.trim(),
        company: company.trim() || undefined,
        facility_count: facilities ? Number(facilities) : undefined,
        message: message.trim(),
        source: "pricing/contact-sales",
      }));
      setSubmitted(true);
      toast.success("Got it — we'll be in touch within one business day.");
    } catch {
      toast.error("Send failed. Email info@nestplot.com directly.");
    } finally {
      setSubmitting(false);
    }
  }

  if (submitted) {
    return (
      <Card>
        <div className="px-6 py-8 text-center">
          <div className="font-display font-extrabold text-[18px] uppercase tracking-[-0.01em]">
            Thanks — we got it.
          </div>
          <p className="text-sub text-sm mt-2">
            We'll be in touch within one business day. For anything urgent,{" "}
            <a href="mailto:info@nestplot.com" className="text-accent font-semibold">
              info@nestplot.com
            </a>
            .
          </p>
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <form onSubmit={onSubmit} className="px-6 py-6 space-y-4">
        <div className="grid md:grid-cols-2 gap-4">
          <Input label="Your name" value={name} onChange={(e) => setName(e.target.value)} required />
          <Input
            label="Work email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <Input label="Company" value={company} onChange={(e) => setCompany(e.target.value)} />
          <Input
            label="How many properties?"
            type="number"
            min={1}
            value={facilities}
            onChange={(e) => setFacilities(e.target.value)}
          />
        </div>
        <Textarea
          label="What can we help with?"
          rows={4}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          required
        />
        <div className="flex items-center gap-3">
          <Btn kind="primary" type="submit" disabled={submitting}>
            {submitting ? "Sending…" : "Send"}
          </Btn>
        </div>
      </form>
    </Card>
  );
}
